/**
 * ScheduleController — /api/v1/equipment/{equipmentId}/schedule
 * Mirrors: src/Aidepo.Api/Controllers/ScheduleController.cs
 *
 * CRUD over the maintenance regimen (ScheduleRule) of one piece of equipment
 * (Phase 3.1, beads hh-3ky). Safety-critical rules cannot be deleted — the
 * backend answers 422; they may only be date-shifted.
 */
import { apiDelete, apiGet, apiPatch, apiPost } from '../client.js';
import type { IsoDateTime, Uuid } from '../types.js';

/** One maintenance rule. `nextAt` is the next due instant. */
export interface ScheduleRule {
  readonly id: Uuid;
  readonly title: string;
  readonly periodMonths: number;
  readonly nextAt: IsoDateTime;
  readonly isSafetyCritical: boolean;
  readonly fromCatalog: boolean;
  /** Дата последнего выполненного ТО, или null/undefined если ещё не делали (aidepo-10). */
  readonly lastCompletedAt?: IsoDateTime | null;
}

export interface AddScheduleRuleRequest {
  readonly title: string;
  readonly periodMonths: number;
  readonly nextAt?: IsoDateTime;
  readonly isSafetyCritical: boolean;
  readonly fromCatalog: boolean;
}

export interface UpdateScheduleRuleRequest {
  readonly title?: string;
  readonly periodMonths?: number;
  readonly nextAt?: IsoDateTime;
  /** true — отметка «Сделано»: бэкенд зафиксирует дату последнего ТО (aidepo-10). */
  readonly completed?: boolean;
}

/** GET /api/v1/equipment/{equipmentId}/schedule — rules ordered by nextAt. */
export function listSchedule(equipmentId: Uuid): Promise<readonly ScheduleRule[]> {
  return apiGet<readonly ScheduleRule[]>(`/api/v1/equipment/${equipmentId}/schedule`);
}

/** POST /api/v1/equipment/{equipmentId}/schedule — add a rule. */
export function addScheduleRule(
  equipmentId: Uuid,
  req: AddScheduleRuleRequest,
): Promise<unknown> {
  return apiPost<unknown, AddScheduleRuleRequest>(
    `/api/v1/equipment/${equipmentId}/schedule`,
    req,
  );
}

/**
 * PATCH /api/v1/equipment/{equipmentId}/schedule/{ruleId} — change title,
 * period and/or shift the next date. Used for the "Перенести" date shift.
 */
export function updateScheduleRule(
  equipmentId: Uuid,
  ruleId: Uuid,
  req: UpdateScheduleRuleRequest,
): Promise<unknown> {
  return apiPatch<unknown, UpdateScheduleRuleRequest>(
    `/api/v1/equipment/${equipmentId}/schedule/${ruleId}`,
    req,
  );
}

/**
 * DELETE /api/v1/equipment/{equipmentId}/schedule/{ruleId}.
 * Safety-critical rules cannot be deleted — the backend returns 422
 * (ApiError with status 422). Callers must guard on `isSafetyCritical`.
 */
export function deleteScheduleRule(equipmentId: Uuid, ruleId: Uuid): Promise<void> {
  return apiDelete(`/api/v1/equipment/${equipmentId}/schedule/${ruleId}`);
}
